/**
 * B7 — OSNAP resolver.
 *
 * Pure function: given the cursor world-pos + active OSNAP modes +
 * spatial index + entity getter, walk every entity whose AABB falls
 * within `aperturePx` of the cursor and emit the closest snap-point
 * candidate. Mirrors AutoCAD AUTOSNAP/APERTURE/OSMODE semantics —
 * marker glyph (AUTOSNAP bit 1), tooltip (bit 2), magnet (bit 4) are
 * the caller's responsibility; this module only locates the snap point.
 *
 * # AutoCAD parity
 *
 *   - 14 modes (no stubs). For each renderable kind we compute every
 *     applicable mode's snap point analytically — line endpoints from
 *     LINE.a/b vertices, circle center/quadrants from CIRCLE.center
 *     /radius, arc midpoint from arc start/end half-angle, polyline
 *     endpoint/midpoint per segment, etc.
 *   - APERTURE is in CSS pixels — converted to world units via
 *     `pixelsPerWorld` so the same hit-test runs at any zoom.
 *   - Mode priority on ties matches the AutoCAD documented preference:
 *     endpoint > midpoint > intersection > center > quadrant >
 *     perpendicular > tangent > node > insertion > nearest > parallel
 *     > extension. `appint` (apparent intersection) and `geomcenter`
 *     share priority with their non-apparent counterparts.
 *   - When two candidates share priority + pixel distance, the earlier
 *     entity (lower owner id lexically) wins — deterministic tie-break.
 */

import {
  appendCandidatesForEntity,
  canEmitExtension,
  type RawHit,
} from "./osnapCandidates";
import {
  appendIntersectionCandidates,
  entityCanProduceIntersectShape,
  INTERSECT_PASS_ENTITY_CAP,
  warnIntersectCapOnce,
} from "./osnapIntersections";
import { resolveExtendedOverride } from "./temporaryOverride";
import { canSnapKind } from "./osnapCapabilities";
import type {
  OsnapCandidate,
  OsnapMode,
  ResolveOsnapInput,
} from "./osnapTypes";
import type { RenderableEntity, Vec2, ViewportRect } from "../types";

export type {
  OsnapCandidate,
  OsnapMode,
  ResolveOsnapInput,
} from "./osnapTypes";

/**
 * AutoCAD OSMODE bit table (AutoCAD 2016+ encoding).
 *   1=END, 2=MID, 4=CENTER, 8=NODE, 16=QUAD, 32=INT,
 *   64=INS, 128=PER, 256=TAN, 512=NEA,
 *   1024=GEOMCENTER, 2048=APPINT, 4096=EXT, 8192=PAR.
 *
 * Bit 16384 is the AutoCAD "OSNAP off" sentinel and is intentionally
 * NOT in this table — callers gate the resolver on whether OSNAP is
 * enabled at all (OSMODE !== 0 with bit 16384 not set).
 */
export const OSMODE_BITS: ReadonlyArray<{ bit: number; mode: OsnapMode }> = [
  { bit: 1, mode: "endpoint" },
  { bit: 2, mode: "midpoint" },
  { bit: 4, mode: "center" },
  { bit: 8, mode: "node" },
  { bit: 16, mode: "quadrant" },
  { bit: 32, mode: "intersection" },
  { bit: 64, mode: "insertion" },
  { bit: 128, mode: "perpendicular" },
  { bit: 256, mode: "tangent" },
  { bit: 512, mode: "nearest" },
  { bit: 1024, mode: "geomcenter" },
  { bit: 2048, mode: "appint" },
  { bit: 4096, mode: "extension" },
  { bit: 8192, mode: "parallel" },
];

/**
 * OSNAP modes that the resolver DECODES but cannot yet resolve to a
 * usable candidate, so they are NOT ready to be offered as live,
 * user-toggleable snaps in the UI. Single source of truth for the
 * DSettings Object-Snap tab's "pending" affordance — keep in sync with
 * the candidate generators (`osnapCandidates.ts` per-entity +
 * `osnapIntersections.ts` pairwise).
 *
 *   - `parallel` (bit 8192) — emits only a degenerate closest-point
 *     (`emit("parallel", near)` in osnapCandidates.ts, LINE + POLYLINE
 *     segments after OSNAP-SNAP-EXPAND) with no reference direction, so it
 *     behaves like low-priority Nearest rather than a real PARallel
 *     constraint (needs the in-progress draft vector). OSNAP-SNAP-EXPAND
 *     widened WHICH kinds emit this degenerate candidate (LINE + POLYLINE —
 *     ARC/ELLIPSE/SPLINE are curves and intentionally excluded, AutoCAD
 *     Parallel requires a LINEAR reference object) but did NOT close this
 *     semantic gap — the remaining pending arm (tracked:
 *     docs/test-gaps/sub-osnap.md).
 *
 * NOTE — `intersection` (bit 32) and `appint` (bit 2048) were un-pended by
 * OSNAP-INTERSECT-EMIT: the resolver now runs a pairwise pass
 * (`appendIntersectionCandidates` in `osnapIntersections.ts`) over the
 * aperture-bounded candidate entities, emitting real crossings for all
 * STRAIGHT + CONIC pairs (LINE / LWPOLYLINE-segment / CIRCLE / ARC).
 * ELLIPSE / SPLINE intersection pairs are the documented follow-up
 * (OSNAP-INTERSECT-EMIT-S2) — the mode is genuinely functional for the
 * common cases (a pair with no computed crossing simply yields no
 * candidate, exactly like hovering where no intersection exists).
 *
 * NOTE — `extension` (bit 4096) is NOT in this set: it resolves to a real,
 * non-degenerate candidate for every kind that has it in
 * `osnapCapabilityMap` (LINE / POLYLINE open ends / ARC curved continuation
 * / genuine ELLIPSE arcs / SPLINE control-polygon-tangent legs —
 * OSNAP-SNAP-EXPAND widened this from LINE-only).
 *
 * When an arm implements `parallel`, remove it from this set and the
 * DSettings checkbox becomes live automatically.
 */
export const OSNAP_MODES_PENDING_IMPLEMENTATION: ReadonlySet<OsnapMode> =
  new Set<OsnapMode>(["parallel"]);

/** Decode an OSMODE bitmask into a Set of active OsnapMode values. */
export function decodeOsmode(osmode: number): Set<OsnapMode> {
  const out = new Set<OsnapMode>();
  for (const { bit, mode } of OSMODE_BITS) {
    if ((osmode & bit) !== 0) out.add(mode);
  }
  return out;
}

/**
 * APPARENT-INTERSECTION candidate-broadening factor.
 *
 * The spatial index returns only entities whose FINITE AABB touches the
 * cursor-aperture box. INTERSECTION needs exactly that (a real crossing lies
 * ON both entities, so both AABBs straddle the crossing → both straddle the
 * cursor when the crossing is under the cursor). APPARENT INTERSECTION,
 * however, snaps where the INFINITE extensions cross — and a clipped segment
 * whose extension passes under the cursor can have an AABB arbitrarily far
 * away (e.g. two short stubs on y=x and y=10−x near the origin whose
 * extensions cross at (5,5)). The narrow aperture query misses those
 * entities, so APPINT silently half-works.
 *
 * A fully correct fix needs an extension-aware index (bucket each segment by
 * its infinite supporting line / each arc by its full circle), which is out
 * of scope for this finding. As the smallest correct improvement we BROADEN
 * the APPINT candidate query to a window `APPINT_BROADEN_FACTOR ×` the
 * aperture half-box, so extension crossings from entities within that larger
 * radius ARE found. The per-crossing distance filter in
 * `appendIntersectionCandidates` stays aperture-bounded, so broadening only
 * changes WHICH entity pairs are considered — never which crossings are
 * offered.
 *
 * RESIDUAL (documented, not silent): an apparent crossing under the cursor is
 * still missed when BOTH source entities lie entirely outside this broadened
 * window (their finite geometry is farther than `factor × aperture` from the
 * cursor while their extensions still cross beneath it). Full coverage is the
 * extension-aware-index follow-up (OSNAP-INTERSECT-EMIT-S2). The per-entity
 * INTERSECTION pass is unaffected — it stays strictly aperture-bounded.
 */
export const APPINT_BROADEN_FACTOR = 16;

/** AUTOSNAP bit positions (1=marker, 2=tooltip, 4=magnet, etc.). */
export const AUTOSNAP_MARKER = 1;
export const AUTOSNAP_TOOLTIP = 2;
export const AUTOSNAP_MAGNET = 4;
export const AUTOSNAP_APERTURE_BOX = 8;

/**
 * Mode priority on ties (lower index = higher priority). Lifted from
 * AutoCAD's documented "highest-priority snap inside the aperture wins"
 * rule.
 *
 * Exported (T8 osnap-query bridge, GitHub Codex PR #528 Finding 3) so a caller that must
 * merge MULTIPLE independent `resolveObjectSnap` results (e.g. `osnapQuery.ts`'s per-space
 * partition merge under `?scope=document`) can reuse this SAME priority table for its own
 * cross-call tie-break, instead of hardcoding a second copy that could silently drift from
 * the real one if the interactive OSNAP priority order is ever revised. Purely additive — no
 * behavior change to any existing caller.
 */
export const MODE_PRIORITY: Record<OsnapMode, number> = {
  endpoint: 0,
  midpoint: 1,
  intersection: 2,
  appint: 2,
  center: 3,
  geomcenter: 3,
  quadrant: 4,
  perpendicular: 5,
  tangent: 6,
  node: 7,
  insertion: 8,
  nearest: 9,
  parallel: 10,
  extension: 11,
};

/**
 * Fraction of the entity-cap budget RESERVED for broadened (extension-only)
 * APPINT candidates. In a dense view the narrow aperture set alone can fill the
 * entire cap with entities that have no apparent crossing under the cursor —
 * starving the two FAR stubs whose infinite extensions actually cross beneath
 * it. Reserving a slice of the budget for broadened ids guarantees those far
 * stubs are still considered.
 *
 * CRITICAL (@codex round-4 #251): the reserve is for broad ids that ACTUALLY
 * RESOLVE — an unresolved (stale / VP-filtered) broad id must NOT strand narrow
 * budget. `gatherAppintEntities` therefore resolves the reserve-worth of broad
 * ids FIRST, then admits narrow up to `cap − (broad that resolved)`, so any
 * unused reserve is BACKFILLED with narrow entities (never wasted on ids that
 * came up `undefined`). With no resolving broad ids the narrow set keeps the
 * whole cap.
 */
export const APPINT_BROADEN_RESERVE_FRACTION = 0.25;

/**
 * WORK bound (total broad ids EXAMINED) for the broadened APPINT id walk
 * (@codex round-6 #1 + #3). It must be DECOUPLED from the SLOT budget
 * (`INTERSECT_PASS_ENTITY_CAP`): a broad id that is SKIPPED — already a narrow
 * candidate (`resolvedIds.has(id)`, #1), already pulled, resolves to `undefined`,
 * or resolves to a NON-INTERSECTABLE entity (point/text/hatch/…, #3) — must NOT
 * consume a slot, but if such skips ALSO cap the walk at the slot budget then a
 * broad window whose prefix is dominated by skips exhausts the budget BEFORE the
 * genuine broad-only stubs after them are ever reached → the reserve is wasted on
 * skips and the far extension-only crossings are starved.
 *
 * The fix: the SLOT loop (`fillBroadTo`) still stops when `broadResolved` reaches
 * its slot target, but the underlying walk may EXAMINE up to this many ids — a
 * generous multiple of the slot cap — so up to this many narrow-dup / undefined /
 * non-intersectable skips can be passed over while still admitting the slot-cap
 * worth of REAL broad-only intersectable stubs. The total work stays bounded (the
 * walk never scans unbounded — ≤ this cap ids examined regardless of skip
 * density), so a pathological dense window cannot pin the frame.
 *
 * Set to `INTERSECT_PASS_ENTITY_CAP × 8`: tolerates a broad prefix up to ~8×
 * dominated by skips before genuine broad-only candidates, which covers the
 * realistic narrow-dup-heavy / non-intersectable-heavy windows while keeping the
 * examination budget a few thousand AABB-tree ids (sub-millisecond).
 */
export const APPINT_BROAD_SCAN_CAP = INTERSECT_PASS_ENTITY_CAP * 8;

/**
 * Gather the APPINT candidate entities: the already-`resolved` aperture
 * entities PLUS any entity within the broadened `APPINT_BROADEN_FACTOR ×`
 * aperture window (so extension-only crossings are found). Entities already
 * in `resolved` are not re-resolved; the broadened query is run only when
 * APPINT is active so the common INTERSECTION-only path pays nothing.
 *
 * CLEAN STREAMING ORDER (@codex round-4 #251 — resolve-broad-first, backfill).
 * The round-3 order admitted narrow up to `cap − reserve` BEFORE knowing how
 * many broad ids actually resolve, so a window of stale/VP-filtered broad ids
 * (all `getEntityById → undefined`) WITHHELD the reserve from the narrow set
 * and then never filled it — a real apparent crossing between narrow entities
 * past the reserve boundary was dropped. The corrected order is:
 *
 *  1. RESOLVE BROAD IDS FIRST, up to the reserve. Count EVERY broad id EXAMINED
 *     as CONSIDERED — including ids already in the narrow set or already seen,
 *     ids that resolve to `undefined` (round-5 #259), and ids that resolve to a
 *     NON-INTERSECTABLE entity (round-6 #3) — so a broad window dominated by such
 *     ids cannot make the scan unbounded: the probe count is bounded by
 *     `APPINT_BROAD_SCAN_CAP` regardless of how many ids are skipped. Only ids
 *     that RESOLVE to a NEW INTERSECTABLE entity (LINE/LWPOLYLINE/CIRCLE/ARC)
 *     consume a reserve SLOT; skipped / `undefined` / non-intersectable ids
 *     consume a considered probe but hold no slot. The WORK bound
 *     (`APPINT_BROAD_SCAN_CAP`) is DECOUPLED from the SLOT cap
 *     (`INTERSECT_PASS_ENTITY_CAP`, round-6 #1) so a prefix dominated by
 *     narrow-dup / non-intersectable skips does not starve the reserve of the
 *     genuine broad-only intersectable stubs that follow them.
 *
 *  2. ADMIT NARROW for the REST of the cap — `cap − (broad that resolved)`. Any
 *     reserve slot a stale broad id failed to fill is BACKFILLED here with a
 *     narrow entity, so narrow budget is never stranded by failed broad
 *     resolves.
 *
 * The final merged set (a) never exceeds the cap, (b) includes the resolved
 * broad ids up to the reserve, (c) backfills any unused reserve with narrow.
 * The same cap is enforced defensively again inside
 * `appendIntersectionCandidates`.
 */
/**
 * Square query box of half-width `half` centred on `center`. One-truth
 * shared by every BROADENED discovery query (`gatherAppintEntities`,
 * `gatherExtensionCurvedEntities`) and the main narrow query below —
 * extracted (@codex round-3 dup:check) so a broadening factor change never
 * has to touch more than one call site's arithmetic.
 */
function queryBoxAround(center: Vec2, half: number): ViewportRect {
  return {
    left: center.x - half,
    top: center.y - half,
    right: center.x + half,
    bottom: center.y + half,
  };
}

function gatherAppintEntities(
  input: ResolveOsnapInput,
  halfBox: number,
  resolved: readonly RenderableEntity[],
): RenderableEntity[] {
  const broadHalf = halfBox * APPINT_BROADEN_FACTOR;
  const broadIds = input.spatialIndex.intersect(
    queryBoxAround(input.cursorWorld, broadHalf),
  );
  const resolvedIds = new Set(resolved.map((e) => e.id));
  // Reserve a slice of the cap for broadened ids. The reserve only ever holds a
  // slot for a broad id that ACTUALLY RESOLVES (step 1 below); slots a stale id
  // fails to fill are backfilled with narrow entities (step 2), so the reserve
  // can never strand narrow budget — and when narrow is short, broad may use
  // MORE than the reserve (step 3), so broad budget is never stranded either.
  const reserve = Math.floor(
    INTERSECT_PASS_ENTITY_CAP * APPINT_BROADEN_RESERVE_FRACTION,
  );
  const seen = new Set<string>();
  const broadResolved: RenderableEntity[] = [];
  // Single lazy walk over the broadened ids, RESUMED across the broad passes (the
  // `considered` budget + the `it` iterator are shared). The `considered`-counter
  // is the WORK bound — bounded by `APPINT_BROAD_SCAN_CAP` (round-6 #1 + #3), a
  // generous multiple of the SLOT cap, so a window whose prefix is dominated by
  // SKIPS (already-narrow / already-seen / `undefined` / non-intersectable ids)
  // does NOT exhaust the budget before the genuine broad-only intersectable stubs
  // after them are reached, yet the scan is still bounded (≤ scan cap ids
  // examined). EVERY examined id consumes a considered probe; a SKIP consumes a
  // probe but holds NO slot, so the slot budget is spent only on real broad-only
  // intersectable candidates.
  const it = broadIds[Symbol.iterator]();
  let considered = 0;
  /** Pull broad ids (resolving each) until `broadResolved` reaches `target`. */
  const fillBroadTo = (target: number): void => {
    while (
      broadResolved.length < target &&
      considered < APPINT_BROAD_SCAN_CAP
    ) {
      const step = it.next();
      if (step.done) break;
      const id = step.value;
      // Count EVERY broad id EXAMINED (round-5 #259): the considered-counter is
      // the WORK bound, so it must increment immediately after `it.next()` —
      // BEFORE the skips. The work bound is `APPINT_BROAD_SCAN_CAP` (round-6 #1),
      // DECOUPLED from the slot cap, so narrow-dup / non-intersectable skips no
      // longer starve the reserve: up to the scan cap ids may be passed over to
      // reach the real broad-only stubs, while the scan stays bounded.
      considered++;
      if (resolvedIds.has(id)) continue; // already a narrow candidate (no slot)
      if (seen.has(id)) continue; // already pulled this broad id (no slot)
      seen.add(id);
      const entity = input.getEntityById(id);
      if (!entity) continue; // stale / VP-filtered → no slot
      // NON-INTERSECTABLE skip (round-6 #3): a resolvable broad entity whose kind
      // cannot yield a straight/conic shape (point/text/hatch/ellipse/spline/…)
      // would never produce an apparent crossing, so it must NOT consume one of
      // the scarce slots — else a broad window dominated by such entities fills
      // the cap with non-intersectable ids and the real far-stub crossings are
      // dropped. It consumes a considered probe (work bound) but holds no slot.
      if (!entityCanProduceIntersectShape(entity)) continue;
      broadResolved.push(entity); // only RESOLVED + INTERSECTABLE ids hold a slot
    }
  };
  // Step 1 — GUARANTEE the reserve: resolve broad ids FIRST up to the reserve,
  // so a dense narrow set can never starve the far extension-only stubs.
  fillBroadTo(reserve);
  // Step 2 — admit narrow entities for the REST of the cap. The limit is
  // `cap − (broad that resolved)`, so any reserve slot a stale broad id failed
  // to fill is BACKFILLED here with a narrow entity (no stranded narrow budget).
  const narrowLimit = INTERSECT_PASS_ENTITY_CAP - broadResolved.length;
  const merged: RenderableEntity[] = [];
  // PRE-CAP TRUNCATION signal (@codex round-5 #2). The entity pre-cap here
  // truncates the broadened+narrow set to `INTERSECT_PASS_ENTITY_CAP` BEFORE
  // `appendIntersectionCandidates` runs; that drop was SILENT (the pass only
  // warns for ITS OWN internal caps, never for entities discarded here). We set
  // `narrowOverflow` when the narrow loop drops a still-available narrow entity
  // because the limit was reached — a real pre-cap drop. (Broad overflow is
  // checked separately below.) This does NOT change WHICH entities are kept (the
  // round-5 reserve/backfill order is settled) — it only ADDS the warn.
  let narrowOverflow = false;
  for (const e of resolved) {
    if (merged.length >= narrowLimit) {
      // The limit was reached with this narrow entity still pending; an un-seen
      // one here would have been admitted but for the cap → a real pre-cap drop.
      // (`break` matches the original immediate-stop work characteristic; a rare
      // already-`seen` duplicate at the boundary simply under-reports the warn,
      // never over-reports — conservative and the warn is once-per-session.)
      if (!seen.has(e.id)) narrowOverflow = true;
      break;
    }
    if (seen.has(e.id)) continue;
    seen.add(e.id);
    merged.push(e);
  }
  // Step 3 — if the narrow set was SHORT, let broad use the remaining cap (so
  // broad budget is never stranded when narrow under-fills). Resume the SAME
  // lazy walk (shared iterator + considered budget) up to the full cap.
  fillBroadTo(INTERSECT_PASS_ENTITY_CAP - merged.length);
  // Append the resolved broad ids after the narrow set; total = narrow +
  // broadResolved ≤ cap by construction (narrow ≤ cap − broadResolved after
  // step 2, and step 3 only raises broadResolved to ≤ cap − merged.length).
  for (const e of broadResolved) merged.push(e);
  // BROAD pre-cap drop (round-5 #2): when the merged set has filled the cap, a
  // RESOLVABLE broad id still left in the iterator would have been admitted but
  // for the cap → a real pre-cap drop. Probe at most one more resolvable broad id
  // (bounded by the SAME shared `considered` budget, so the work bound holds; the
  // cap is full so we never admit it — only flag the overflow).
  let broadOverflow = false;
  // SCAN-CAP truncation WITH FREE SLOTS (#348). Round-6 decoupled the broad-scan
  // WORK bound (`APPINT_BROAD_SCAN_CAP`) from the SLOT bound, so the walk can stop
  // because the WORK budget ran out while `merged.length` is STILL below the slot
  // cap — when the broad result STARTS with `APPINT_BROAD_SCAN_CAP` worth of stale
  // / narrow-dup / non-intersectable ids that exhaust the scan budget BEFORE the
  // real intersectable broad stubs after them are reached. The slot-full overflow
  // path below is gated on `merged.length >= INTERSECT_PASS_ENTITY_CAP`, so this
  // free-slots scan-cap exhaustion fired NO warn → a SILENT cap-induced miss (the
  // later intersectable stubs were never considered and APPINT can return null).
  // Treat scan-cap exhaustion as its OWN overflow condition: when the walk stopped
  // because the scan budget was spent (`considered >= APPINT_BROAD_SCAN_CAP`) AND
  // slots remain free AND the iterator is NOT exhausted (a remaining id MIGHT be a
  // real intersectable stub the scan never reached), surface the warn — OUTSIDE the
  // slot-full guard. Conservative (one UN-resolved peek): a remaining stale/dup id
  // over-reports the once-per-session warn, never under-reports a real drop; we do
  // NOT resolve past the scan cap and do NOT change WHICH entities are kept.
  let scanCapOverflow = false;
  if (
    merged.length < INTERSECT_PASS_ENTITY_CAP &&
    considered >= APPINT_BROAD_SCAN_CAP
  ) {
    const peek = it.next();
    if (!peek.done) scanCapOverflow = true;
  }
  // Skip the broad peek when the narrow loop already flagged a drop (the warn
  // fires regardless) — saves the extra iterator/resolve probes.
  if (!narrowOverflow && merged.length >= INTERSECT_PASS_ENTITY_CAP) {
    while (considered < APPINT_BROAD_SCAN_CAP) {
      const peek = it.next();
      if (peek.done) break;
      const pid = peek.value;
      considered++;
      if (resolvedIds.has(pid) || seen.has(pid)) continue;
      seen.add(pid);
      const pe = input.getEntityById(pid);
      // Only a resolvable AND intersectable broad id would have been admitted but
      // for the cap (round-6 #3: non-intersectable ids hold no slot, so dropping
      // them is NOT a real pre-cap drop). A non-intersectable / stale id consumes
      // a considered probe but does not flag overflow.
      if (pe && entityCanProduceIntersectShape(pe)) {
        broadOverflow = true; // a resolvable+intersectable broad candidate was dropped
        break;
      }
    }
    // Broad-alone-fills-cap (final-state review): when broad RESOLUTION itself
    // exhausted the shared `considered` work budget, the bounded peek above
    // never ran (or ran out of budget on stale ids), so a still-open iterator
    // means the cap dropped a candidate. One un-RESOLVED `it.next()` detects a
    // remaining id WITHOUT resolving past the cap — conservative: a remaining
    // stale/dup id over-reports the once-per-session warn, never under-reports
    // a real drop (the prior silent-drop bug when ≥cap broad ids resolved).
    if (!broadOverflow && considered >= APPINT_BROAD_SCAN_CAP) {
      const peek = it.next();
      if (!peek.done) broadOverflow = true;
    }
  }
  // SURFACE the pre-cap truncation (round-5 #2 + #348): warn via the SAME
  // once-per-session guard the pass uses — the drop is no longer silent — when
  // EITHER (a) the merged set filled the SLOT cap and a real candidate (narrow or
  // broad) was dropped at it, OR (b) the broad walk hit the WORK (scan) cap with
  // FREE SLOTS and a remaining id the scan never reached (#348 — a scan-cap
  // truncation that leaves slots unfilled is still a cap-induced miss, even though
  // the slot cap was never reached).
  if (
    (merged.length >= INTERSECT_PASS_ENTITY_CAP &&
      (broadOverflow || narrowOverflow)) ||
    scanCapOverflow
  ) {
    warnIntersectCapOnce(
      `[osnap] APPINT broadened candidate set capped at ${INTERSECT_PASS_ENTITY_CAP} entities; some apparent-intersection pairs may be omitted`,
    );
  }
  return merged;
}

/**
 * Test-only seam exposing `gatherAppintEntities` so a test can pass a
 * DETERMINISTIC `resolved` (narrow) entity array and assert the merged set
 * directly — without depending on RBush's bbox-tree return order (@codex
 * round-4 #251 guard). The round-3 bug withheld the reserve from the narrow set
 * even when every broad id is stale; this seam lets a test prove that the
 * crossing pair placed PAST the round-3 reserve boundary is now admitted (the
 * merged set == the full narrow set, length == min(narrow, cap), reserve fully
 * backfilled), which the RBush-ordered `resolveObjectSnap` test could not pin
 * deterministically. NOT used in production.
 */
export function __gatherAppintEntitiesForTest(
  input: ResolveOsnapInput,
  halfBox: number,
  resolved: readonly RenderableEntity[],
): RenderableEntity[] {
  return gatherAppintEntities(input, halfBox, resolved);
}

/**
 * EXTENSION-BROADEN candidate-gathering — every EXTENSION-capable kind
 * (@codex round-3 P2 found the ARC/ELLIPSE half of this gap; @codex
 * round-6 P2, found by a further local review of OSNAP-SNAP-EXPAND round-4,
 * found LINE/POLYLINE/SPLINE have the SAME reachability gap for a
 * non-axis-aligned segment — the round-3 doc's "flush with the entity's own
 * tight AABB by construction" claim only holds for a HORIZONTAL or VERTICAL
 * segment, where the infinite-line continuation literally coincides with
 * one of the box's (degenerate) edges).
 *
 * Production ARC/ELLIPSE `RenderableEntity.bounds` is an EXACT sweep-tight
 * box (`apps/web/src/app/scene3d/adapters/sceneGeometryBuilders.ts`
 * `buildArcSceneGeometry` / `buildEllipseSceneGeometry` — endpoints + only
 * the quadrant extrema that fall ON the visible sweep; ellipse additionally
 * samples 32 points), NOT a full-circle/full-ellipse box. Its curved
 * continuation moves AWAY from the tight AABB (the tangent at an endpoint is
 * perpendicular to the radius), so a cursor sitting a normal,
 * AutoCAD-consistent distance along the visible curve's continuation can be
 * well outside `cursor±apertureWorld` of the tight AABB even though the
 * actual extension point IS within `apertureWorld` of the cursor.
 *
 * LINE / POLYLINE segments and SPLINE control-polygon-tangent legs have an
 * analogous gap for a DIAGONAL direction: `extensionFoot` projects the
 * cursor onto the segment's INFINITE supporting line, "before"/"after" the
 * finite endpoint. For a horizontal or vertical segment that continuation
 * stays exactly on the (degenerate, zero-thickness) AABB edge, so the
 * narrow `cursor±apertureWorld` box overlapping the extension point ALSO
 * overlaps the AABB — no gap. For a diagonal segment (e.g. a 45° polyline
 * leg), the AABB is a genuine rectangle and the line cuts across it
 * corner-to-corner; continuing PAST an open end moves the extension point
 * away from the rectangle in BOTH axes at once, the same way an arc's
 * tangent moves away from its sweep-tight box — a cursor sitting exactly ON
 * that diagonal continuation, a plausible distance past the open vertex,
 * can fall entirely outside `cursor±apertureWorld` of the AABB while still
 * being within `apertureWorld` of the true extension point.
 *
 * In every case the entity is then never even considered (never resolved,
 * `appendCandidatesForEntity` never runs for it), so the EXTENSION
 * candidate this slice added was silently unreachable at ordinary
 * APERTURE/zoom settings for the affected geometry.
 *
 * Broadens the discovery query the SAME way APPINT already does
 * (`APPINT_BROADEN_FACTOR` precedent) — only WHICH entities are considered
 * changes; the per-candidate `apertureWorld` distance gate inside
 * `appendCandidatesForEntity`/`emit()` still bounds the final EMITTED point.
 * Scoped to every kind `osnapCapabilityMap` marks EXTENSION-capable (LINE /
 * POLYLINE / ARC / ELLIPSE / SPLINE — checked via `canSnapKind`, the SAME
 * one-truth table the running-snap emit gate uses, so this broadening can
 * never silently drift behind a future capability-map change); every other
 * kind (CIRCLE, POINT, block-insert, text, the compound kinds, …) is
 * unaffected and unbroadened. A simple bounded cap (no reserve/backfill
 * budget-sharing like APPINT's — this is a single-purpose per-entity
 * gather, not a pairwise search competing for a shared budget) keeps a
 * dense broadened window's worst case bounded.
 *
 * RESIDUAL (documented, not silent — @codex round-14 P2): this is the SAME
 * architectural tradeoff `APPINT_BROADEN_FACTOR` above already documents and
 * accepts. The query is still `cursor ± factor × apertureWorld` — a FIXED
 * radius around the cursor — so an entity whose finite AABB sits farther than
 * that from the cursor is never even considered, no matter how close the
 * cursor sits to that entity's true (infinite) extension point. Concretely: an
 * open polyline from (0,0) to (10,0) with `apertureWorld = 1` and cursor at
 * (-20,0) has its extension foot exactly AT the cursor (a legitimate
 * AutoCAD-consistent EXTENSION hit — the cursor is right on the infinite
 * continuation, well within aperture of the true foot), but the broadened
 * query box is `x=[-36,-4]`, which does not overlap the polyline's `x=[0,10]`
 * AABB at all — the entity is silently never resolved, independent of the
 * per-candidate `apertureWorld` gate inside `appendCandidatesForEntity`, which
 * never gets a chance to run for it. Pinned by
 * `resolveObjectSnap.test.ts` ("misses an extension point whose cursor
 * distance exceeds EXTENSION_BROADEN_FACTOR × aperture, per the documented
 * residual" / "finds it just inside the same broadened window").
 *
 * A fully correct fix needs the SAME extension-aware index APPINT's residual
 * calls for (bucket each segment by its infinite supporting line, each
 * arc/ellipse by its full circle/ellipse, each spline by its tangent rays) —
 * out of scope for this finding, tracked under the same
 * OSNAP-INTERSECT-EMIT-S2 follow-up (`docs/test-gaps/sub-osnap.md`) since one
 * index would close both residuals at once. Bumping this constant would only
 * move the boundary, not remove it, so it is left at the same value as its
 * APPINT twin rather than tuned as a stopgap.
 */
export const EXTENSION_BROADEN_FACTOR = 16;
export const EXTENSION_BROADEN_CAP = 200;

/**
 * @codex round-8 P2 — WORK bound for `gatherExtensionCurvedEntities`'s scan
 * over the broadened window, DECOUPLED from `EXTENSION_BROADEN_CAP` (the
 * SLOT bound on genuine candidates collected), mirroring the same
 * scan-cap/slot-cap split `gatherAppintEntities` already uses
 * (`APPINT_BROAD_SCAN_CAP` precedent). Before this, `considered` was BOTH
 * the work counter AND the implicit slot counter in one variable — a dense
 * run of KIND-eligible but INSTANCE-incapable ids (closed polylines, full
 * ellipses; `canSnapKind` is kind-level and can't see this) ahead of a
 * genuine broad-only extension candidate in spatial-index order could
 * exhaust the cap on ids that were NEVER going to be pushed to `out`,
 * silently starving the real candidate. A generous multiple (matching
 * APPINT's `× 8`) lets the scan walk past many non-emitting instances while
 * still collecting at most `EXTENSION_BROADEN_CAP` real candidates.
 */
export const EXTENSION_BROADEN_SCAN_CAP = EXTENSION_BROADEN_CAP * 8;

/**
 * @codex round-4 P2 — the broadened entities
 * `gatherExtensionCurvedEntities` returns are, BY CONSTRUCTION, entities the
 * NARROW aperture query did NOT surface (`resolvedIds.has(id)` is filtered
 * out above). Every other active mode's semantic contract is "the entity's
 * AABB overlaps `cursor±apertureWorld`" — passing the caller's full
 * `input.activeModes` into `appendCandidatesForEntity` for these broad-only
 * entities let a co-active mode (e.g. `center`) emit from an entity the
 * narrow query intentionally excluded, purely because `extension` also
 * happened to be on (a small arc whose sweep-tight AABB sits far from the
 * cursor could suddenly yield a `center` snap that `center` ALONE would
 * never have produced). Restrict the broadened pass to EXTENSION only — the
 * one mode whose broadened discovery this gather exists for.
 */
const EXTENSION_ONLY_MODES: ReadonlySet<OsnapMode> = new Set(["extension"]);

function gatherExtensionCurvedEntities(
  input: ResolveOsnapInput,
  halfBox: number,
  resolved: readonly RenderableEntity[],
): RenderableEntity[] {
  const broadHalf = halfBox * EXTENSION_BROADEN_FACTOR;
  const broadIds = input.spatialIndex.intersect(
    queryBoxAround(input.cursorWorld, broadHalf),
  );
  const resolvedIds = new Set(resolved.map((e) => e.id));
  const out: RenderableEntity[] = [];
  // `i` is the WORK bound (ids examined, capped at EXTENSION_BROADEN_SCAN_CAP)
  // — separate from `out.length`, the SLOT bound (genuine candidates
  // collected, capped at EXTENSION_BROADEN_CAP). @codex round-8 P2: without
  // this split, ids that fail `canEmitExtension` (kind-eligible but
  // instance-incapable — a closed polyline, a full ellipse) still consumed
  // the SAME budget as a real candidate, so enough of them ahead of a
  // genuine broad-only entity could exhaust the cap before it was reached.
  let i = 0;
  for (; i < broadIds.length; i++) {
    if (out.length >= EXTENSION_BROADEN_CAP || i >= EXTENSION_BROADEN_SCAN_CAP) {
      break;
    }
    const id = broadIds[i];
    if (resolvedIds.has(id)) continue; // already in the narrow set
    const entity = input.getEntityById(id);
    if (!entity) continue; // stale / VP-filtered
    if (!canSnapKind(entity.geometry.kind, "extension")) {
      continue; // only EXTENSION-capable KINDS can have an unreachable extension foot
    }
    if (!canEmitExtension(entity)) {
      continue; // kind-eligible but THIS instance has no open end (closed polyline / full ellipse)
    }
    // @codex round-11 P2 — `canEmitExtension` only proves the entity CAN
    // emit an extension candidate in general (not closed / not a full
    // sweep); it says nothing about whether THIS cursor's extension point
    // for THIS entity actually lands within `apertureWorld` (halfBox — a
    // kind/instance-eligible line whose extension foot happens to fall far
    // from the cursor is exactly as "unproductive" as the round-8 closed-
    // polyline case, just discovered per-cursor instead of per-instance).
    // Probe with a throwaway buffer so an entity that would emit NOTHING
    // for this cursor never consumes a slot — mirrors the round-8 fix's
    // reasoning one level deeper.
    const probe: RawHit[] = [];
    appendCandidatesForEntity(
      entity,
      input.cursorWorld,
      EXTENSION_ONLY_MODES,
      halfBox,
      probe,
    );
    if (probe.length === 0) continue; // eligible kind/instance, but no in-aperture hit for THIS cursor
    out.push(entity);
  }
  if (i < broadIds.length) {
    // Either cap truncated the scan before `broadIds` was exhausted — a
    // real extension candidate may have been past this point. Surfaced
    // (not silent) via the SAME rate-limited channel APPINT's pre-cap
    // already shares (`warnIntersectCapOnce` doc, osnapIntersections.ts).
    warnIntersectCapOnce(
      `[osnap] EXTENSION broadened candidate scan capped at ${EXTENSION_BROADEN_SCAN_CAP} ids / ${EXTENSION_BROADEN_CAP} candidates; some extension snaps may be omitted`,
    );
  }
  return out;
}

/**
 * Test-only seam exposing `gatherExtensionCurvedEntities`, mirroring
 * `__gatherAppintEntitiesForTest`. NOT used in production.
 */
export function __gatherExtensionCurvedEntitiesForTest(
  input: ResolveOsnapInput,
  halfBox: number,
  resolved: readonly RenderableEntity[],
): RenderableEntity[] {
  return gatherExtensionCurvedEntities(input, halfBox, resolved);
}

/**
 * Resolve the nearest snap-point candidate to the cursor, if any. The
 * returned candidate's `world` is the world-space snap target; the
 * caller projects it back to client coords for AUTOSNAP-bit-4 magnet.
 *
 * Returns `null` when:
 *   - `activeModes` is empty (OSMODE = 0), OR
 *   - no entity AABB falls within `aperturePx` of the cursor (unless APPINT
 *     or EXTENSION is active, each of which broadens its own discovery
 *     query — `gatherAppintEntities` / `gatherExtensionCurvedEntities`), OR
 *   - every per-entity candidate computed lands outside the aperture.
 */
export function resolveObjectSnap(
  input: ResolveOsnapInput,
): OsnapCandidate | null {
  // OSNAP-OVERRIDE-MODES — a DEFERRED Extended (Apparent) Intersection override
  // with a CAPTURED first operand SUPERSEDES the running snaps for this pick
  // (AutoCAD parity: an active object-snap override wins over running OSNAP).
  // While the first operand is still un-picked (`firstOwnerId === null`) the
  // running-snap path below stays live so the user can land the first pick.
  if (input.extendedOverride && input.extendedOverride.firstOwnerId !== null) {
    return resolveExtendedOverride(input);
  }
  if (input.activeModes.size === 0) return null;
  if (input.aperturePx <= 0) return null;
  const ppw = Math.max(input.pixelsPerWorld, 1e-12);
  const apertureWorld = input.aperturePx / ppw;
  const halfBox = apertureWorld;
  const candidateIds = input.spatialIndex.intersect(
    queryBoxAround(input.cursorWorld, halfBox),
  );
  // When APPINT or EXTENSION is active the broadened queries below can
  // surface entities even if the narrow aperture box is empty, so only
  // short-circuit on an empty narrow query when NEITHER is requested.
  if (
    candidateIds.length === 0 &&
    !input.activeModes.has("appint") &&
    !input.activeModes.has("extension")
  ) {
    return null;
  }
  // Opt 3 — `intersect()` returns ids only. Resolve every id to a
  // RenderableEntity via the explicit resolver from the caller
  // (`input.getEntityById`); an id that resolves to nothing is stale.
  //
  // CACHE-DIRECT STUBS (Codex PR #784 round-8 P2) — a resolved record is NOT
  // guaranteed to carry real geometry. On the cache-direct path the index's
  // resolver is `renderableById` chained with `createMetadataStubResolver`
  // (`state/spatialBuilder.ts`), so an id the LOD pre-classify dropped as
  // sub-pixel resolves to a METADATA STUB: real `id`/`type`/`layer`/`bounds`,
  // but a degenerate `point` geometry at the bbox CENTRE. Such a record must
  // contribute no snap candidate — the renderer draws nothing for it and its
  // lone position is not geometry — which is enforced ONE level down, in
  // `appendCandidatesForEntity`'s `case "point"` arm
  // (`state/metadataStub.ts isMetadataStubRenderable`), so every caller of that
  // generator (this narrow loop AND the EXTENSION broadened probe) is covered
  // by one gate. The pairwise INTERSECTION / APPINT pass needs no gate of its
  // own: `entityCanProduceIntersectShape` already rejects every `point`-kind
  // record, stub or not. Stubs stay in `resolved` (id/bounds are honest) so the
  // broadened gathers below still see them as already-visited ids.
  const out: RawHit[] = [];
  // Keep the resolved entities so the pairwise INTERSECTION / APPINT pass
  // below can re-walk PAIRS without a second `getEntityById` round-trip
  // (the per-entity loop has already paid for resolution).
  const resolved: RenderableEntity[] = [];
  for (const id of candidateIds) {
    const entity = input.getEntityById(id);
    if (!entity) continue;
    resolved.push(entity);
    appendCandidatesForEntity(
      entity,
      input.cursorWorld,
      input.activeModes,
      apertureWorld,
      out,
    );
  }
  // OSNAP-INTERSECT-EMIT — pairwise crossing pass. Per-entity generators
  // (`appendCandidatesForEntity`) run once per entity and so can never see
  // a crossing across a PAIR. INTERSECTION (bit 32) / APPARENT
  // INTERSECTION (bit 2048) need the entity pairs, so they live here.
  // PERF: only run when the mode is active; the candidate set is already
  // aperture-bounded (small N), so O(N²) over it is fine (a defensive cap
  // inside `appendIntersectionCandidates` warns + bounds a pathological N).
  // COVERAGE: all STRAIGHT + CONIC pairs (LINE / LWPOLYLINE-segment /
  // CIRCLE / ARC); ELLIPSE / SPLINE intersection pairs are the documented
  // OSNAP-INTERSECT-EMIT-S2 follow-up — for those a pair simply yields no
  // candidate (same as hovering where no crossing exists), NOT a no-op.
  if (input.activeModes.has("intersection")) {
    appendIntersectionCandidates(
      resolved,
      input.cursorWorld,
      "intersection",
      apertureWorld,
      out,
    );
  }
  if (input.activeModes.has("appint")) {
    // APPINT snaps on INFINITE extensions, so a clipped segment whose
    // extension passes under the cursor can have an AABB far outside the
    // narrow aperture box (and be absent from `resolved`). Broaden the
    // candidate query (see APPINT_BROADEN_FACTOR) so those extension-only
    // pairs are found. The per-crossing distance filter in
    // `appendIntersectionCandidates` keeps the EMITTED points aperture-bound.
    const appintEntities = gatherAppintEntities(
      input,
      halfBox,
      resolved,
    );
    appendIntersectionCandidates(
      appintEntities,
      input.cursorWorld,
      "appint",
      apertureWorld,
      out,
    );
  }
  if (input.activeModes.has("extension")) {
    // OSNAP-SNAP-EXPAND @codex round-3/round-6 P2 — EXTENSION continues past
    // an entity's exact tight AABB for EVERY EXTENSION-capable kind (ARC/
    // ELLIPSE via curved continuation, LINE/POLYLINE/SPLINE via a diagonal
    // infinite-line continuation — see `gatherExtensionCurvedEntities`
    // doc), so a narrow-only query can miss an entity whose extension
    // candidate IS within `apertureWorld` of the cursor. Broaden the same
    // way APPINT does. The per-candidate distance gate inside
    // `appendCandidatesForEntity`/`emit()` still bounds the EMITTED point —
    // broadening only changes WHICH entities are considered.
    const extensionEntities = gatherExtensionCurvedEntities(
      input,
      halfBox,
      resolved,
    );
    for (const entity of extensionEntities) {
      appendCandidatesForEntity(
        entity,
        input.cursorWorld,
        EXTENSION_ONLY_MODES,
        apertureWorld,
        out,
      );
    }
  }
  if (out.length === 0) return null;
  let best: RawHit | null = null;
  for (const h of out) {
    if (best === null) {
      best = h;
      continue;
    }
    const pBest = MODE_PRIORITY[best.mode];
    const pH = MODE_PRIORITY[h.mode];
    if (pH < pBest) {
      best = h;
    } else if (pH === pBest) {
      if (h.distWorld < best.distWorld - 1e-12) {
        best = h;
      } else if (
        Math.abs(h.distWorld - best.distWorld) <= 1e-12 &&
        h.ownerEntityId < best.ownerEntityId
      ) {
        best = h;
      }
    }
  }
  if (!best) return null;
  return {
    kind: best.mode,
    world: best.world,
    ownerEntityId: best.ownerEntityId,
    distancePxFromCursor: best.distWorld * ppw,
  };
}
